import React, { useMemo, useRef, useState } from 'react'
import {
  IAnomaliesV2UserFilter,
  IAnomaliesV2UserFiltersResponse,
  TAnomaliesV2UserFilterScope,
} from '../shared/types'
import { useApiGet, useModal, useOutsideClick, varClass } from '../shared'
import { ConfirmModal } from './ConfirmModal'
import { FilterGroup } from './shared'
import {
  UserFilterValues,
  createUserFilter,
  deleteUserFilter,
  updateUserFilter,
} from './api'

// Saved filter presets for the triage + rejection-review tabs. Backend
// persists arbitrary `values` bags per (user, scope) with at most one default
// per scope. We treat `values` as exactly the querystring params the data
// endpoints accept, so applying a preset is a single setSearchParams-style
// call in the parent.

interface PresetsState {
  presets: IAnomaliesV2UserFilter[] | null
  refetch: () => void
}

// Wraps the GET endpoint with a refetch trigger so the parent can re-pull
// after a mutation. Returning null on first load lets the dropdown render a
// disabled stub instead of empty controls.
export const useUserFilters = (
  scope: TAnomaliesV2UserFilterScope
): PresetsState => {
  const [refetchKey, setRefetchKey] = useState(0)
  const data = useApiGet<IAnomaliesV2UserFiltersResponse>(
    '/anomalies/v2/user-filters',
    { scope },
    [scope, refetchKey]
  )
  return useMemo(
    () => ({
      presets: data?.filters ?? null,
      refetch: () => setRefetchKey((k) => k + 1),
    }),
    [data]
  )
}

// Resolve the default preset (if any). Helpful for the first-visit auto-apply
// branch in each tab.
export const findDefaultPreset = (
  presets: IAnomaliesV2UserFilter[] | null
): IAnomaliesV2UserFilter | null => {
  if (!presets) return null
  return presets.find((p) => p.isDefault) ?? null
}

// Deep-ish equality for the values bag — keys + stringified primitives. Good
// enough for "is the current filter state still the preset I picked?" without
// dragging in a dep.
const valuesEqual = (
  a: UserFilterValues | undefined,
  b: UserFilterValues | undefined
): boolean => {
  if (!a || !b) return false
  const ak = Object.keys(a)
  const bk = Object.keys(b)
  if (ak.length !== bk.length) return false
  for (const k of ak) {
    if (String(a[k]) !== String(b[k])) return false
  }
  return true
}

export const PresetsControl = (props: {
  scope: TAnomaliesV2UserFilterScope
  // Current filter state translated into the same shape the preset stores.
  // The control uses this for two things: showing whether the active preset
  // is "dirty", and as the body of the Save action.
  currentValues: UserFilterValues
  // Apply a preset's values back into the parent state. The parent owns the
  // state↔values mapping so it can ignore unknown keys gracefully.
  onApply: (values: UserFilterValues) => void
  presetsState: PresetsState
}) => {
  const { scope, currentValues, onApply, presetsState } = props
  const { presets, refetch } = presetsState
  const { showModal, hideModal } = useModal()

  const [open, setOpen] = useState(false)
  const wrapRef = useRef<HTMLDivElement>(null)
  useOutsideClick(wrapRef, () => setOpen(false))

  // Best-effort "which preset am I on right now?" — exact match of values
  // bag. If the user has tweaked anything, no preset is active and the
  // overwrite-Save button stays disabled.
  const activePreset = useMemo(() => {
    if (!presets) return null
    return presets.find((p) => valuesEqual(p.values, currentValues)) ?? null
  }, [presets, currentValues])

  const buttonLabel = activePreset ? activePreset.name : 'No preset'

  const openSaveAs = () => {
    showModal(
      <PresetSaveModal
        scope={scope}
        values={currentValues}
        existingNames={(presets ?? []).map((p) => p.name)}
        hideModal={hideModal}
        onSaved={refetch}
      />
    )
    setOpen(false)
  }

  const overwriteCurrent = async () => {
    if (!activePreset) return
    await updateUserFilter(activePreset.uuid, { values: currentValues })
    refetch()
    setOpen(false)
  }

  const setAsDefault = async (uuid: string) => {
    await updateUserFilter(uuid, { isDefault: true })
    refetch()
  }

  const clearDefault = async (uuid: string) => {
    await updateUserFilter(uuid, { isDefault: false })
    refetch()
  }

  const remove = (preset: IAnomaliesV2UserFilter) => {
    showModal(
      <ConfirmModal
        title="Delete preset"
        body={
          <>
            Delete the preset <b>{preset.name}</b>
            {preset.isDefault ? ' (your default for this tab)' : ''}? This
            can&rsquo;t be undone.
          </>
        }
        confirmLabel="Delete preset"
        hideModal={hideModal}
        onConfirm={async () => {
          await deleteUserFilter(preset.uuid)
          refetch()
        }}
      />
    )
    setOpen(false)
  }

  const rename = (preset: IAnomaliesV2UserFilter) => {
    showModal(
      <PresetRenameModal
        preset={preset}
        existingNames={(presets ?? [])
          .filter((p) => p.uuid !== preset.uuid)
          .map((p) => p.name)}
        hideModal={hideModal}
        onSaved={refetch}
      />
    )
    setOpen(false)
  }

  return (
    <FilterGroup label="PRESET">
      <div className="anomalies-presets-wrap" ref={wrapRef}>
        <button
          type="button"
          className={varClass({
            'anomalies-presets-trigger': true,
            'anomalies-presets-trigger-active': !!activePreset,
          })}
          onClick={() => setOpen((v) => !v)}
          title={activePreset ? `Applied: ${activePreset.name}` : undefined}
        >
          {activePreset?.isDefault && (
            <span
              className="anomalies-presets-star"
              title="Default for this tab"
            >
              ★
            </span>
          )}
          <span className="anomalies-presets-trigger-label">
            {buttonLabel}
          </span>
          <span className="anomalies-presets-trigger-chevron">▾</span>
        </button>

        {open && (
          <div className="anomalies-presets-popover">
            <div className="anomalies-presets-popover-list">
              {presets === null && (
                <div className="anomalies-presets-popover-empty">Loading…</div>
              )}
              {presets && presets.length === 0 && (
                <div className="anomalies-presets-popover-empty">
                  No saved presets yet.
                </div>
              )}
              {(presets ?? []).map((p) => {
                const isActive = activePreset?.uuid === p.uuid
                return (
                  <div
                    key={p.uuid}
                    className={varClass({
                      'anomalies-presets-row': true,
                      'anomalies-presets-row-active': isActive,
                    })}
                  >
                    <button
                      type="button"
                      className="anomalies-presets-row-apply"
                      onClick={() => {
                        onApply(p.values)
                        setOpen(false)
                      }}
                      title={`Apply "${p.name}"`}
                    >
                      <span className="anomalies-presets-row-star">
                        {p.isDefault ? '★' : ''}
                      </span>
                      <span className="anomalies-presets-row-name">
                        {p.name}
                      </span>
                    </button>
                    <div className="anomalies-presets-row-actions">
                      {p.isDefault ? (
                        <button
                          type="button"
                          className="anomalies-presets-row-action"
                          onClick={() => clearDefault(p.uuid)}
                          title="Stop auto-applying this preset on tab open"
                        >
                          Unset default
                        </button>
                      ) : (
                        <button
                          type="button"
                          className="anomalies-presets-row-action"
                          onClick={() => setAsDefault(p.uuid)}
                          title="Auto-apply on tab open"
                        >
                          Set default
                        </button>
                      )}
                      <button
                        type="button"
                        className="anomalies-presets-row-action"
                        onClick={() => rename(p)}
                      >
                        Rename
                      </button>
                      <button
                        type="button"
                        className="anomalies-presets-row-action anomalies-presets-row-action-danger"
                        onClick={() => remove(p)}
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                )
              })}
            </div>
            <div className="anomalies-presets-popover-footer">
              <button
                type="button"
                className="btn btn-default btn-small"
                onClick={overwriteCurrent}
                disabled={!activePreset}
                title={
                  activePreset
                    ? `Overwrite "${activePreset.name}" with current filters`
                    : 'Apply a preset first, then Save will overwrite it'
                }
              >
                Save
              </button>
              <button
                type="button"
                className="btn btn-primary btn-small"
                onClick={openSaveAs}
              >
                Save as…
              </button>
            </div>
          </div>
        )}
      </div>
    </FilterGroup>
  )
}

// ---------------------------------------------------------------------------
// Save-as modal
// ---------------------------------------------------------------------------

const PresetSaveModal = (props: {
  scope: TAnomaliesV2UserFilterScope
  values: UserFilterValues
  existingNames: string[]
  hideModal: () => void
  onSaved: () => void
}) => {
  const { scope, values, existingNames, hideModal, onSaved } = props
  const [name, setName] = useState('')
  const [isDefault, setIsDefault] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const trimmed = name.trim()
  // Soft duplicate warning — backend doesn't enforce uniqueness, but two
  // "Mobile" entries in the dropdown look like a bug. We surface it as a hint
  // and let the user proceed anyway.
  const dupWarning = useMemo(
    () =>
      trimmed && existingNames.some((n) => n.toLowerCase() === trimmed.toLowerCase())
        ? `A preset named "${trimmed}" already exists. Saving will create a second one.`
        : null,
    [trimmed, existingNames]
  )

  const submit = async () => {
    if (!trimmed) {
      setError('Please give the preset a name.')
      return
    }
    setSubmitting(true)
    setError(null)
    try {
      await createUserFilter({
        scope,
        name: trimmed,
        isDefault,
        values,
      })
      onSaved()
      hideModal()
    } catch (e: any) {
      setError(e?.message || 'Failed to save preset. Try again.')
      setSubmitting(false)
    }
  }

  return (
    <div className="card-body anomalies-preset-modal">
      <div className="text-dark text-bigger text-bolder m-b-3">
        Save current filters as a preset
      </div>
      <p className="text-light text-small m-b-3">
        Saves the active filter selection so you can recall it later. Date
        ranges aren't saved — the preset always opens on the latest data.
      </p>

      <div className="m-b-3">
        <div className="text-dark text-small text-bolder m-b-1">Name</div>
        <div className="control" style={{ display: 'block' }}>
          <input
            type="text"
            autoFocus
            value={name}
            onChange={(e) => setName(e.target.value)}
            maxLength={120}
            placeholder="e.g. Direct partners — Mobile"
            style={{ display: 'block', width: '100%', boxSizing: 'border-box' }}
          />
        </div>
        {dupWarning && (
          <div className="text-light text-small m-t-1">{dupWarning}</div>
        )}
      </div>

      <div className="m-b-3">
        <label className="anomalies-preset-modal-checkbox">
          <input
            type="checkbox"
            checked={isDefault}
            onChange={(e) => setIsDefault(e.target.checked)}
          />
          <span>
            Set as default — applies automatically on first visit each session.
          </span>
        </label>
      </div>

      {error && <div className="text-danger text-small m-b-2">{error}</div>}

      <div className="row row-end" style={{ gap: 8 }}>
        <button
          type="button"
          className="btn btn-default"
          onClick={hideModal}
          disabled={submitting}
        >
          Cancel
        </button>
        <button
          type="button"
          className="btn btn-primary"
          onClick={submit}
          disabled={submitting}
        >
          {submitting ? 'Saving…' : 'Save preset'}
        </button>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Rename modal — single field, no value changes
// ---------------------------------------------------------------------------

const PresetRenameModal = (props: {
  preset: IAnomaliesV2UserFilter
  existingNames: string[]
  hideModal: () => void
  onSaved: () => void
}) => {
  const { preset, existingNames, hideModal, onSaved } = props
  const [name, setName] = useState(preset.name)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const trimmed = name.trim()
  const dupWarning = useMemo(
    () =>
      trimmed && existingNames.some((n) => n.toLowerCase() === trimmed.toLowerCase())
        ? `A preset named "${trimmed}" already exists.`
        : null,
    [trimmed, existingNames]
  )

  const submit = async () => {
    if (!trimmed) {
      setError('Name cannot be empty.')
      return
    }
    if (trimmed === preset.name) {
      hideModal()
      return
    }
    setSubmitting(true)
    setError(null)
    try {
      await updateUserFilter(preset.uuid, { name: trimmed })
      onSaved()
      hideModal()
    } catch (e: any) {
      setError(e?.message || 'Failed to rename preset. Try again.')
      setSubmitting(false)
    }
  }

  return (
    <div className="card-body anomalies-preset-modal">
      <div className="text-dark text-bigger text-bolder m-b-3">
        Rename preset
      </div>
      <div className="m-b-3">
        <div className="text-dark text-small text-bolder m-b-1">Name</div>
        <div className="control" style={{ display: 'block' }}>
          <input
            type="text"
            autoFocus
            value={name}
            onChange={(e) => setName(e.target.value)}
            maxLength={120}
            style={{ display: 'block', width: '100%', boxSizing: 'border-box' }}
          />
        </div>
        {dupWarning && (
          <div className="text-light text-small m-t-1">{dupWarning}</div>
        )}
      </div>
      {error && <div className="text-danger text-small m-b-2">{error}</div>}
      <div className="row row-end" style={{ gap: 8 }}>
        <button
          type="button"
          className="btn btn-default"
          onClick={hideModal}
          disabled={submitting}
        >
          Cancel
        </button>
        <button
          type="button"
          className="btn btn-primary"
          onClick={submit}
          disabled={submitting}
        >
          {submitting ? 'Saving…' : 'Save'}
        </button>
      </div>
    </div>
  )
}
